import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  useTheme,
  alpha,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getQuiz } from "../../state/slices/quiz";
import { startAttempt, submitAnswer, submitAttempt, getCompletedAttemptByQuiz } from "../../state/slices/quizAttempt";
import Loading from "../../components/loading/loading";

const TakeQuiz = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentQuiz, loading: quizLoading } = useSelector((state) => state.quiz);
  const { currentAttempt, completedAttempt, loading: attemptLoading, errorMsg } = useSelector((state) => state.quizAttempt);
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeSpent, setTimeSpent] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    dispatch(getQuiz(id));
    dispatch(getCompletedAttemptByQuiz(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (completedAttempt) {
      // Redirect to results if already completed
      navigate(`/quiz/results/${completedAttempt._id}`);
      return;
    }
    if (currentQuiz && !currentAttempt && !completedAttempt) {
      dispatch(startAttempt(id)).catch((error) => {
        // Error will be handled by errorMsg in state
      });
    }
  }, [currentQuiz, id, dispatch, currentAttempt, completedAttempt, navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeSpent(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
    if (currentAttempt) {
      dispatch(submitAnswer({ attemptId: currentAttempt._id, questionId, answer }));
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < (currentQuiz?.questions?.length || 0) - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = () => {
    setShowSubmitDialog(true);
  };

  const confirmSubmit = async () => {
    setShowSubmitDialog(false);
    if (currentAttempt) {
      try {
        const result = await dispatch(submitAttempt({ attemptId: currentAttempt._id, timeSpent }));
        if (result && result.type && result.type.includes('fulfilled')) {
          navigate(`/quiz/results/${currentAttempt._id}`);
        }
      } catch (error) {
        console.error('Error submitting quiz:', error);
      }
    }
  };

  if (quizLoading && !currentQuiz) {
    return <Loading />;
  }

  if (!currentQuiz) {
    return <Loading />;
  }

  if (errorMsg && !currentAttempt) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          pb: 4,
        }}
      >
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
          {completedAttempt && (
            <Button variant="contained" onClick={() => navigate(`/quiz/results/${completedAttempt._id}`)}>
              {t('quiz.viewResults')}
            </Button>
          )}
        </Container>
      </Box>
    );
  }

  const currentQuestion = currentQuiz.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / currentQuiz.questions.length) * 100;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="md" sx={{ pt: 4 }}>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}
        <Card>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6">{currentQuiz.title}</Typography>
                <Typography variant="body2">
                  {t('quiz.question')} {currentQuestionIndex + 1} {t('common.of')} {currentQuiz.questions.length}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>

            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {currentQuestion.question}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {currentQuestion.points} {t('quiz.points')}
              </Typography>

              {currentQuestion.type === "multipleChoice" && (
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <RadioGroup
                    value={answers[currentQuestion._id] || ""}
                    onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                  >
                    {currentQuestion.options.map((option, index) => (
                      <FormControlLabel
                        key={index}
                        value={option}
                        control={<Radio />}
                        label={option}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}

              {currentQuestion.type === "trueFalse" && (
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <RadioGroup
                    value={answers[currentQuestion._id] === true ? "true" : answers[currentQuestion._id] === false ? "false" : ""}
                    onChange={(e) => {
                      // Convert string to boolean for consistent handling
                      const boolValue = e.target.value === "true";
                      handleAnswerChange(currentQuestion._id, boolValue);
                    }}
                  >
                    <FormControlLabel value="true" control={<Radio />} label={t('quiz.true')} />
                    <FormControlLabel value="false" control={<Radio />} label={t('quiz.false')} />
                  </RadioGroup>
                </FormControl>
              )}

              {(currentQuestion.type === "shortAnswer" || currentQuestion.type === "essay") && (
                <TextField
                  fullWidth
                  multiline
                  rows={currentQuestion.type === "essay" ? 6 : 3}
                  value={answers[currentQuestion._id] || ""}
                  onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder={t('quiz.typeYourAnswer')}
                />
              )}
            </Card>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0 || attemptLoading}
              >
                {t('quiz.previous')}
              </Button>
              {currentQuestionIndex === currentQuiz.questions.length - 1 ? (
                <Button 
                  variant="contained" 
                  onClick={handleSubmit}
                  disabled={attemptLoading}
                >
                  {t('quiz.submitQuiz')}
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  disabled={attemptLoading}
                >
                  {t('quiz.next')}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)}>
        <DialogTitle>{t('quiz.submitQuiz')}?</DialogTitle>
        <DialogContent>
          <Typography>{t('quiz.submitQuizConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={confirmSubmit} variant="contained">{t('common.submit')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TakeQuiz;

